import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiFilter } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient.js";
import { handleLoadingAndErrorState } from "#/redux/util/reduxUtils";
import { getErrorMessage } from "#/utils/APIUtils.js";

const NAME = "ocenjevalniListAdmin";
const basePath = "/api/dog/ocenjevanje/list/ocenjevalni-list";

export const fetchOcenjevalniListAdminPage = createAsyncThunk(
  `${NAME}/fetchPage`,
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      return ApiClient.get(
        `${basePath}/page`,
        undefined,
        page,
        filter,
        sort
      ).then(({ data }) => data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchOcenjevalniListAdminById = createAsyncThunk(
  `${NAME}/fetchById`,
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${basePath}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const createOcenjevalniListAdmin = createAsyncThunk(
  `${NAME}/create`,
  async ({ dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(basePath, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateOcenjevalniListAdmin = createAsyncThunk(
  `${NAME}/update`,
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`${basePath}/${id}`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteOcenjevalniListAdmin = createAsyncThunk(
  `${NAME}/delete`,
  async ({ id, version }, { rejectWithValue }) => {
    try {
      await ApiClient.delete(`${basePath}/${id}`, { version });
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchListSklopiLink = createAsyncThunk(
  `${NAME}/fetchListSklopiLink`,
  async ({ ocenjevalniListId, ocenjevalniSklopId }, { rejectWithValue }) => {
    try {
      const filter = new ApiFilter()
        .equal("ocenjevalniListId", ocenjevalniListId)
        .equal("ocenjevalniSklopId", ocenjevalniSklopId);

      const response = await ApiClient.get(
        "/api/dog/ocenjevanje/list/ocenjevalniList/sklop/page",
        undefined,
        { page: 0, size: 10 },
        filter
      );
      return response?.data?.content?.[0] ?? null;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchSklopElementiLinks = createAsyncThunk(
  `${NAME}/fetchSklopElementiLinks`,
  async ({ sklopId, ocenaMeritevId }, { rejectWithValue }) => {
    try {
      const filter = new ApiFilter().equal("sklopId", sklopId);
      if (ocenaMeritevId) {
        filter.equal("ocenaMeritevId", ocenaMeritevId);
      }

      const response = await ApiClient.get(
        "/api/dog/ocenjevanje/list/ocenjevalniSklop/element/page",
        undefined,
        { page: 0, size: 500 },
        filter
      );
      return response?.data?.content ?? [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteListSklopiLink = createAsyncThunk(
  `${NAME}/deleteListSklopiLink`,
  async ({ id, version }, { rejectWithValue }) => {
    try {
      await ApiClient.delete(
        `/api/dog/ocenjevanje/list/ocenjevalniList/sklop/${id}`,
        { version }
      );
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteSklopElementiLink = createAsyncThunk(
  `${NAME}/deleteSklopElementiLink`,
  async ({ id, version }, { rejectWithValue }) => {
    try {
      await ApiClient.delete(
        `/api/dog/ocenjevanje/list/ocenjevalniSklop/element/${id}`,
        { version }
      );
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteOcenjevalniSklopAdmin = createAsyncThunk(
  `${NAME}/deleteSection`,
  async ({ id, version }, { rejectWithValue }) => {
    try {
      await ApiClient.delete(
        `/api/dog/ocenjevanje/list/ocenjevalniSklop/${id}`,
        { version }
      );
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteOcenaMeritevAdmin = createAsyncThunk(
  `${NAME}/deleteField`,
  async ({ id, version }, { rejectWithValue }) => {
    try {
      await ApiClient.delete(`/api/dog/ocenjevanje/list/ocenaMeritev/${id}`, {
        version,
      });
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const thunks = [
  fetchOcenjevalniListAdminPage,
  fetchOcenjevalniListAdminById,
  createOcenjevalniListAdmin,
  updateOcenjevalniListAdmin,
  deleteOcenjevalniListAdmin,
  fetchListSklopiLink,
  fetchSklopElementiLinks,
  deleteListSklopiLink,
  deleteSklopElementiLink,
  deleteOcenjevalniSklopAdmin,
  deleteOcenaMeritevAdmin,
];

const initialState = {
  isLoading: false,
  error: null,
  page: { content: [], totalElements: 0, totalPages: 0, number: 0, size: 25 },
  byId: {},
  details: null,
  lastSaved: null,
};

const ocenjevalniListAdminSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    clearOcenjevalniListAdminDetails(state) {
      state.details = null;
    },
    clearOcenjevalniListAdminStatus(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOcenjevalniListAdminPage.fulfilled, (state, action) => {
        state.page = action.payload ?? initialState.page;
        action.payload?.content?.forEach((item) => {
          if (item?.id) {
            state.byId[item.id] = item;
          }
        });
      })
      .addCase(fetchOcenjevalniListAdminById.fulfilled, (state, action) => {
        const item = action.payload ?? null;
        state.details = item;
        if (item?.id) {
          state.byId[item.id] = item;
        }
      })
      .addCase(deleteOcenjevalniListAdmin.fulfilled, (state, action) => {
        const deletedId = action.payload;
        if (deletedId) {
          delete state.byId[deletedId];
          if (state.details?.id === deletedId) {
            state.details = null;
          }
          state.page = {
            ...state.page,
            content: (state.page?.content || []).filter(
              (item) => item?.id !== deletedId
            ),
          };
        }
      })
      .addCase(createOcenjevalniListAdmin.fulfilled, (state, action) => {
        const item = action.payload ?? null;
        state.lastSaved = item;
        state.details = item;
        if (item?.id) {
          state.byId[item.id] = item;
        }
      })
      .addCase(updateOcenjevalniListAdmin.fulfilled, (state, action) => {
        const item = action.payload ?? null;
        state.lastSaved = item;
        state.details = item;
        if (item?.id) {
          state.byId[item.id] = item;
        }
      });

    handleLoadingAndErrorState(builder, thunks);
  },
});

export const {
  clearOcenjevalniListAdminDetails,
  clearOcenjevalniListAdminStatus,
} = ocenjevalniListAdminSlice.actions;

export default ocenjevalniListAdminSlice.reducer;
